import React, { useEffect, useMemo, useState } from 'react'

import {
	ArrowLeft,
	ChevronRight,
	Crown,
	Loader2,
	MoreVertical,
	Pencil,
} from 'lucide-react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/core/hooks/useAuth'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { AccountTab } from '@/features/account/types'
import { openChatbase } from '@/shared/analytics/ChatbaseWidget'
import { Button } from '@/shared/components/atoms/Button'
import Icon from '@/shared/components/atoms/Icon'
import { Separator } from '@/shared/components/atoms/Separator'
import { AccountSelectorModal } from '@/shared/components/organisms/AccountSelectorModal/AccountSelectorModal'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import Billing from './billing/components/Billing'
import AccountNavigation from './components/AccountNavigation'
import BrandProfile from './components/BrandProfile'
import MusicLegalCompliance from './components/MusicLegalCompliance'
import Subscription from './components/Subscription'
import UserProfile from './components/UserProfile'
import Users from './components/Users'

interface AccountPageUIProps {
	activeTab: AccountTab
	setActiveTab: (tab: AccountTab) => void
}

const AccountPageUI: React.FC<AccountPageUIProps> = ({
	activeTab,
	setActiveTab,
}) => {
	const { t, changeLanguage, currentLanguage } = useTranslation('common')
	const navigate = useNavigate()
	const location = useLocation()
	const { logout } = useAuth()

	const { user, isLoading, error } = useSelector(
		(state: RootState) => state.auth
	)
	const { isBillingEnabled, hasAccountSelectorAccess, features } =
		usePermission()
	const currentAccount = useCurrentAccount()
	const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false)
	const [mobileActiveTab, setMobileActiveTab] = useState<AccountTab | null>(
		null
	)
	const [isMobileUserProfileEditing, setIsMobileUserProfileEditing] =
		useState(false)
	const [isMobileBrandProfileEditing, setIsMobileBrandProfileEditing] =
		useState(false)
	const [mobileUsersActionsSignal, setMobileUsersActionsSignal] = useState(0)

	const userCountry = user?.account?.country
	const isSoundreef = user?.account?.royalty?.name === 'Soundreef'
	const hasOrganizations = userCountry
		? [
				'AR',
				'MX',
				'ES',
				'CO',
				'CL',
				'PA',
				'PE',
				'EC',
				'BO',
				'CR',
				'DO',
				'SV',
				'GT',
				'HN',
				'NI',
				'UY',
				'VE',
			].includes(userCountry)
		: false
	const isComplianceEnabled = features.account.musicLegalCompliance
	const showMusicLegalCompliance =
		isComplianceEnabled && (isSoundreef || hasOrganizations)
	const showSubscriptionSettings = isBillingEnabled
	const accountSearchParams = useMemo(
		() => new URLSearchParams(location.search),
		[location.search]
	)
	const billingViewParam = accountSearchParams.get('view')
	const billingActionParam = accountSearchParams.get('action')

	useEffect(() => {
		if (
			(activeTab === 'billing' || activeTab === 'subscription') &&
			!showSubscriptionSettings
		) {
			setActiveTab('user-profile')
		}
	}, [activeTab, setActiveTab, showSubscriptionSettings])

	const canAccessAccountSelector = useMemo(() => {
		return hasAccountSelectorAccess()
	}, [hasAccountSelectorAccess])

	const accountTabs = useMemo(
		() =>
			[
				{
					id: 'user-profile' as AccountTab,
					label: t('account.navigation.user_profile'),
					visible: true,
				},
				{
					id: 'brand-profile' as AccountTab,
					label: t('account.navigation.brand_profile'),
					visible: true,
				},
				{
					id: 'subscription' as AccountTab,
					label: t('account.navigation.subscription'),
					visible: showSubscriptionSettings,
				},
				{
					id: 'billing' as AccountTab,
					label: t('account.navigation.billing'),
					visible: showSubscriptionSettings,
				},
				{
					id: 'users' as AccountTab,
					label: t('account.navigation.users'),
					visible: true,
				},
				{
					id: 'music-legal-compliance' as AccountTab,
					label: t('account.navigation.music_legal_compliance'),
					visible: showMusicLegalCompliance,
				},
			].filter((tab) => tab.visible),
		[showSubscriptionSettings, showMusicLegalCompliance, t]
	)

	const accountPlan = useMemo(() => {
		const activeSubscription = currentAccount?.getActiveSubscriptions()?.[0]
		const trialSubscription = currentAccount?.getTrialSubscriptions()?.[0]
		return activeSubscription?.plan?.name || trialSubscription?.plan?.name
	}, [currentAccount])

	const toggleLanguage = () => {
		const nextLanguage = currentLanguage?.startsWith('es') ? 'en' : 'es'
		changeLanguage(nextLanguage)
	}

	const handleSettingsBack = () => {
		if (mobileActiveTab === 'billing' && billingViewParam) {
			const params = new URLSearchParams(location.search)
			params.delete('view')
			params.delete('action')
			params.delete('method')
			const search = params.toString()
			navigate(
				{
					pathname: location.pathname,
					search: search ? `?${search}` : '',
				},
				{ replace: false }
			)
			return
		}

		if (mobileActiveTab !== null) {
			setMobileActiveTab(null)
			setIsMobileUserProfileEditing(false)
			setIsMobileBrandProfileEditing(false)
			return
		}

		if (window.history.length > 1) {
			navigate(-1)
			return
		}

		navigate('/zones')
	}

	const mobileHeaderTitle = useMemo(() => {
		if (!mobileActiveTab) {
			return t('account.mobile.title')
		}

		if (mobileActiveTab === 'billing') {
			if (billingViewParam === 'billing-details') {
				return t('account.billing.mobile.details_title')
			}
			if (billingViewParam === 'payment-methods') {
				return billingActionParam === 'add'
					? t('account.billing.mobile.add_payment_method_title')
					: t('account.billing.mobile.payment_methods_title')
			}
		}

		return accountTabs.find((tab) => tab.id === mobileActiveTab)?.label || ''
	}, [mobileActiveTab, billingViewParam, billingActionParam, t, accountTabs])

	const renderActiveTab = () => {
		switch (activeTab) {
			case 'user-profile':
				return (
					<UserProfile
						requestMobileEdit={isMobileUserProfileEditing}
						onMobileEditingChange={(isEditing) =>
							setIsMobileUserProfileEditing(isEditing)
						}
					/>
				)
			case 'brand-profile':
				return (
					<BrandProfile
						requestMobileEdit={isMobileBrandProfileEditing}
						onMobileEditingChange={(isEditing) =>
							setIsMobileBrandProfileEditing(isEditing)
						}
					/>
				)
			case 'subscription':
				return <Subscription />
			case 'billing':
				return <Billing />
			case 'users':
				return <Users mobileActionsSignal={mobileUsersActionsSignal} />
			case 'music-legal-compliance':
				return <MusicLegalCompliance />
			default:
				return <UserProfile />
		}
	}

	return (
		<>
			<PageLayout
				customHeader={
					<>
						<div className="flex items-center justify-between gap-3 border-b border-border md:border-none lg:hidden">
							<div className="flex items-center py-4 md:py-0 gap-3">
								<button
									onClick={handleSettingsBack}
									className="inline-flex items-center justify-center w-8 h-8 text-foreground hover:text-muted-foreground transition-colors"
									aria-label={t('actions.back')}
								>
									<ArrowLeft className="w-5 h-5" />
								</button>
								<h1 className="text-lg text-foreground">{mobileHeaderTitle}</h1>
							</div>
							{mobileActiveTab === null && (
								<Button
									variant="ghost"
									size="icon"
									onClick={() => logout()}
									className="w-12 h-12 rounded-full bg-card"
									aria-label={t('navigation.logout')}
								>
									<Icon
										name="logout"
										width="20px"
										height="20px"
										className="ml-1"
									/>
								</Button>
							)}
							{mobileActiveTab === 'user-profile' &&
								!isMobileUserProfileEditing && (
									<Button
										variant="ghost"
										size="icon"
										onClick={() => setIsMobileUserProfileEditing(true)}
										className="w-11 h-11 rounded-full border border-border bg-card"
										aria-label={t('actions.edit')}
									>
										<Pencil className="w-4 h-4" />
									</Button>
								)}
							{mobileActiveTab === 'brand-profile' &&
								!isMobileBrandProfileEditing && (
									<Button
										variant="ghost"
										size="icon"
										onClick={() => setIsMobileBrandProfileEditing(true)}
										className="w-11 h-11 rounded-full border border-border bg-card"
										aria-label={t('actions.edit')}
									>
										<Pencil className="w-4 h-4" />
									</Button>
								)}
							{mobileActiveTab === 'billing' &&
								billingViewParam === 'billing-details' &&
								billingActionParam !== 'edit-billing' && (
									<Button
										variant="ghost"
										size="icon"
										onClick={() => {
											const params = new URLSearchParams(location.search)
											params.set('action', 'edit-billing')
											const search = params.toString()
											navigate(
												{
													pathname: location.pathname,
													search: search ? `?${search}` : '',
												},
												{ replace: false }
											)
										}}
										className="w-11 h-11 rounded-full border border-border bg-card"
										aria-label={t('actions.edit')}
									>
										<Pencil className="w-4 h-4" />
									</Button>
								)}
							{mobileActiveTab === 'users' && (
								<Button
									variant="ghost"
									size="icon"
									onClick={() =>
										setMobileUsersActionsSignal((value) => value + 1)
									}
									className="w-11 h-11 rounded-full border border-border bg-card mr-2"
									aria-label={t('account.navigation.users')}
								>
									<MoreVertical className="w-4 h-4" />
								</Button>
							)}
						</div>
						<div className="hidden lg:flex items-center justify-between">
							<h2 className="flex items-center text-2xl font-normal tracking-tight md:text-3xl">
								{t('account.title')}
							</h2>
						</div>
					</>
				}
				contentClassName="px-0 py-0 lg:px-8 lg:py-6"
			>
				<div className="lg:hidden">
					{mobileActiveTab === null ? (
						<>
							<div className="px-4 pt-4 pb-2">
								<div className="flex flex-wrap items-center gap-2">
									<div className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 bg-card">
										<span className="text-sm font-medium text-foreground">
											{user?.firstname ||
												user?.lastname ||
												currentAccount?.name}
										</span>
										{accountPlan && (
											<span className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary px-2 py-0.5 text-xs">
												<Crown className="w-3 h-3" />
												{accountPlan}
											</span>
										)}
									</div>
								</div>
							</div>

							<div className="px-4 py-4 pb-28">
								<div className="mb-2 text-sm text-muted-foreground">
									{t('account.mobile.sections.account')}
								</div>
								<nav className="mb-6">
									{accountTabs.map((tab) => (
										<React.Fragment key={tab.id}>
											<button
												onClick={() => {
													setActiveTab(tab.id)
													setMobileActiveTab(tab.id)
													setIsMobileUserProfileEditing(false)
													setIsMobileBrandProfileEditing(false)
												}}
												className="w-full flex items-center justify-between py-3 text-left text-foreground"
											>
												<span>{tab.label}</span>
												<ChevronRight className="w-4 h-4 text-muted-foreground" />
											</button>

											{/* Mobile-only support entry, placed right after Users */}
											{tab.id === 'users' && (
												<button
													onClick={() => {
														openChatbase()
													}}
													className="w-full flex items-center justify-between py-3 text-left text-foreground"
												>
													<span>{t('account.mobile.support_chat')}</span>
													<ChevronRight className="w-4 h-4 text-muted-foreground" />
												</button>
											)}
										</React.Fragment>
									))}
									{canAccessAccountSelector && (
										<button
											onClick={() => setIsAccountSelectorOpen(true)}
											className="w-full flex items-center justify-between py-3 text-left text-foreground"
										>
											<span>{t('navigation.switch_account')}</span>
											<ChevronRight className="w-4 h-4 text-muted-foreground" />
										</button>
									)}
								</nav>

								<div className="mb-2 text-sm text-muted-foreground">
									{t('account.mobile.sections.extras')}
								</div>
								<nav>
									<button
										onClick={() => navigate('/integrations')}
										className="w-full flex items-center justify-between py-3 text-left text-foreground"
									>
										<span>{t('integrations.title')}</span>
										<ChevronRight className="w-4 h-4 text-muted-foreground" />
									</button>
									<button
										onClick={toggleLanguage}
										className="w-full flex items-center justify-between py-3 text-left text-foreground"
									>
										<span>{t('account.mobile.change_language')}</span>
										<ChevronRight className="w-4 h-4 text-muted-foreground" />
									</button>
								</nav>
							</div>
						</>
					) : (
						<div className="px-4 py-4 pb-28">
							{isLoading && (
								<div className="flex items-center justify-center py-12">
									<Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
								</div>
							)}

							{error && (
								<div className="flex items-center justify-center py-12">
									<p className="text-destructive">
										{t('account.error_loading_data')}
									</p>
								</div>
							)}

							{!isLoading && !error && renderActiveTab()}
						</div>
					)}
				</div>

				<div className="hidden lg:flex lg:flex-row flex-col">
					<div className="w-full lg:w-64 lg:p-6 px-2 py-2 lg:border-r border-border">
						<div className="mb-6">
							<h2 className="text-lg font-semibold text-foreground">
								{user?.firstname} {user?.lastname}
							</h2>
						</div>
						<AccountNavigation
							activeTab={activeTab}
							onTabChange={setActiveTab}
						/>
					</div>

					<Separator className="block lg:hidden" />

					<div className="flex-1 px-2 py-4 lg:p-6">
						{isLoading && (
							<div className="flex items-center justify-center py-12">
								<Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
							</div>
						)}

						{error && (
							<div className="flex items-center justify-center py-12">
								<p className="text-destructive">
									{t('account.error_loading_data')}
								</p>
							</div>
						)}

						{!isLoading && !error && renderActiveTab()}
					</div>
				</div>
			</PageLayout>

			{canAccessAccountSelector && (
				<AccountSelectorModal
					open={isAccountSelectorOpen}
					onOpenChange={setIsAccountSelectorOpen}
				/>
			)}
		</>
	)
}

export default AccountPageUI
